/**
 * 장착 아이템 판별
 *
 * 서버는 장착 여부를 별도 필드로 보내지 않는다. `S_InvList`의 `statusForPacket`
 * 바이트는 축복/저주/미감정/봉인만 담고, 장착 상태는 **아이템 표시명 끝에 붙는
 * 접미사**로만 전달된다 (L1ItemInstance.getNumberedViewName):
 *
 *   무기(type2 = 1)          → " ($9)"    装備(Armed)
 *   방어구(type2 = 2), 펫아이템 → " ($117)"  装備(Worn)
 *
 * `$9` / `$117`은 원본 클라이언트 리소스의 문자열 키라서 웹 클라이언트에서는
 * 그대로 노출하면 안 된다. 판별에 쓰고 표시명에서는 떼어낸다.
 */

import type { WeaponType } from './spriteLoader';
import { WEAPON_TYPES } from '../data/weaponTypes';

/** 표시명 끝의 장착 접미사. 무기 ($9) / 방어구·펫아이템 ($117). */
const EQUIPPED_SUFFIX = /\s*\(\$(?:9|117)\)\s*$/;

/** 무기 장착 접미사만. 방어구($117)와 달리 이것이 붙은 것은 손에 든 무기다. */
const ARMED_SUFFIX = /\s*\(\$9\)\s*$/;

/** 표시명에 장착 접미사가 붙어 있는지. */
export function isEquippedName(name: string): boolean {
  return EQUIPPED_SUFFIX.test(name ?? '');
}

/** 표시명에서 장착 접미사를 떼어낸다. 없으면 원본을 그대로 돌려준다. */
export function stripEquippedSuffix(name: string): string {
  return (name ?? '').replace(EQUIPPED_SUFFIX, '');
}

/**
 * 인벤토리에서 **지금 손에 든 무기**의 종류를 알아낸다.
 *
 * 서버는 장착 무기를 따로 알려주지 않으므로 인벤토리 표시명에서 역산한다.
 * 무기 장착 접미사 `($9)`가 붙은 항목을 찾고, 남은 `$숫자` 키를 무기 표에서
 * 조회한다. 표에 없더라도 `($9)`가 붙었다는 것 자체가 무기를 들었다는 뜻이므로
 * 근접 무기로 본다 — 활은 표에 모두 들어 있어 여기서 새지 않는다.
 *
 * @returns 장착 무기의 스프라이트 종류. 무기를 들지 않았으면 `'none'`(맨손).
 */
export function equippedWeaponType(items: Array<{ name?: string }>): WeaponType {
  let fallback: WeaponType | null = null;

  for (const item of items ?? []) {
    const name = item?.name ?? '';
    if (!ARMED_SUFFIX.test(name)) continue;

    const key = name.replace(ARMED_SUFFIX, '').trim();
    const known = WEAPON_TYPES[key];
    // 표에 있는 것을 먼저 믿는다. `($9)`는 무기 표에 없는 물건(화살·스팅 등)에도
    // 붙을 수 있는데, 그것이 인벤토리에서 앞선다는 이유로 진짜 무기를 가리면
    // 활을 든 채 근접으로 판정되는 식으로 어긋난다.
    if (known) return known;
    if (fallback === null) fallback = 'sword';
  }

  return fallback ?? 'none';
}
